import { FilterMatchMode } from "primereact/api";
import { Card } from "primereact/card";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ReportJobBatchEntry } from "../../../../client";
import { LOB } from "../../../../api/routes";
import { useQueueJobs } from "../../rater/useIntegrationJobs";
import { ROUTES } from "../../../../routes";
import { useLocalUser, useSafeReports } from "../../data";
import styles from "./shared.module.css";
import { formatTimestamp } from "./submissionHistoryUtils";

type StatusLabel = "In Progress" | "Pending" | "Partially Running";

type QueueRow = ReportJobBatchEntry & {
  company_name: string;
  min_position: number | null;
  position_display: string;
  status_label: StatusLabel;
};

const TYPE_OPTIONS = [
  "ADDRESS_INTEGRATION",
  "ICM",
  "POLICY_FLY",
  "RATER",
  "RERUN_REPORT",
  "RMS",
];

const STATUS_OPTIONS = ["In Progress", "Pending", "Partially Running"];

const defaultFilters: DataTableFilterMeta = {
  integration_type: { value: null, matchMode: FilterMatchMode.EQUALS },
  status_label: { value: null, matchMode: FilterMatchMode.EQUALS },
};

const typeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
  <Dropdown
    value={options.value}
    options={TYPE_OPTIONS}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder="All Types"
    showClear
    style={{ minWidth: "150px" }}
  />
);

const statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
  <Dropdown
    value={options.value}
    options={STATUS_OPTIONS}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder="All Statuses"
    showClear
    style={{ minWidth: "150px" }}
  />
);

export const JobsInQueue = () => {
  const user = useLocalUser();
  const lobsForUser = (user?.lobs ?? []) as LOB[];
  const reports = useSafeReports(lobsForUser).data ?? [];

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);

  const reportIds = useMemo(() => reports.map((r) => r.report_id), [reports]);

  const companyNameByReportId = useMemo(
    () =>
      Object.fromEntries(
        reports.map((r) => [
          r.report_id,
          r.report_metadata.company_info.company_name,
        ])
      ),
    [reports]
  );

  const { data: jobs = [], isLoading } = useQueueJobs(reportIds, [
    "pending",
    "in_progress",
  ]);

  const rows: QueueRow[] = useMemo(() => {
    return jobs.map((job) => {
      const positions = job.queue_positions.filter(
        (p): p is number => p != null
      );
      const nullCount = job.queue_positions.length - positions.length;

      const min = positions.length > 0 ? Math.min(...positions) : null;
      const max = positions.length > 0 ? Math.max(...positions) : null;

      const position_display: string =
        positions.length === 0 ? "—" : min === max ? `${min}` : `${min}–${max}`;

      const status_label: StatusLabel =
        nullCount > 0 && positions.length > 0
          ? "Partially Running"
          : positions.length === 0
            ? "In Progress"
            : "Pending";

      return {
        ...job,
        company_name: companyNameByReportId[job.report_id],
        min_position: min,
        position_display,
        status_label,
      };
    });
  }, [jobs, companyNameByReportId]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        // in_progress (null position) before pending (has position)
        if (a.min_position == null && b.min_position != null) return -1;
        if (a.min_position != null && b.min_position == null) return 1;
        if (a.min_position != null && b.min_position != null)
          return a.min_position - b.min_position;
        // both null (in_progress) — sort by started_at
        return a.started_at.localeCompare(b.started_at);
      }),
    [rows]
  );

  return (
    <div className={styles.page}>
      <Card title="Jobs in Queue">
        {isLoading ? (
          <div className={styles.centered}>
            <ProgressSpinner />
          </div>
        ) : (
          <DataTable
            value={sortedRows}
            filters={filters}
            filterDisplay="menu"
            onFilter={(e) => setFilters(e.filters)}
            emptyMessage="No jobs in queue"
            scrollable
            scrollHeight="calc(100vh - 300px)"
            stripedRows
          >
            <Column
              field="min_position"
              header="Position"
              sortable
              style={{ width: "100px" }}
              body={(row: QueueRow) => row.position_display}
            />
            <Column
              field="status_label"
              header="Status"
              filter
              filterElement={statusFilterTemplate}
              showFilterMatchModes={false}
              sortable
              style={{ width: "200px" }}
              body={(row: QueueRow) => (
                <Tag
                  value={row.status_label}
                  severity={
                    row.status_label === "Pending"
                      ? "warning"
                      : row.status_label === "Partially Running"
                        ? "warning"
                        : "info"
                  }
                />
              )}
            />
            <Column
              field="integration_type"
              header="Type"
              filter
              filterElement={typeFilterTemplate}
              showFilterMatchModes={false}
              sortable
              style={{ width: "200px" }}
            />
            <Column
              field="company_name"
              header="Company"
              sortable
              style={{ minWidth: "200px" }}
              body={(row: QueueRow) => (
                <Link to={ROUTES.DASHBOARD_REPORT(row.report_id)}>
                  {row.company_name}
                </Link>
              )}
            />
            <Column
              field="started_at"
              header="Started"
              sortable
              style={{ width: "200px" }}
              body={(row: QueueRow) => formatTimestamp(row.started_at)}
            />
            <Column
              header="Jobs"
              style={{ width: "80px" }}
              body={(row: QueueRow) => row.responses.length}
            />
          </DataTable>
        )}
      </Card>
    </div>
  );
};
